import { html, nothing } from '@shared/utils/rendering/html';
import Form from '@shared/components/forms';
import Button from '@shared/components/buttons';

import { useRef, useCallback, useMemo } from 'preact/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ReviewEvidenceFormOptions } from '../../../../../../configs/forms/subcomponents/review-evidence-form.config';
import { ReviewEvidenceFormSchema } from '../../../../../../configs/forms/subcomponents/review-evidence-form.schema';

import { useCache } from '../../../../../../utils/context/cache-context';
import { CacheKeyNames } from '../../../../../../constants/utility/cache-key-names';
import { RatingsFromId } from '../../../../../../constants/chips/ratings.constants';
import { onSuccessfulResponse, upsertOne } from '@core/utils/helpers/mutations/mutation-effects';
import { usePatch } from '@core/utils/hooks/use-patch';
import { updateEvidence } from './helpers/cache-updates';

const ReviewEvidenceForm = ({ rowData, ...props }) => {
    const getCache = useCache().getKey;
    const queryClient = useQueryClient();

    if (props?.readonly && !rowData?.evidence?.evidenceId) {
        return html`<div class="card-header"><h4>No Evidence was Captured</h4></div>
            <div class="card-body">
                The evidence for this review is not available, or was not captured during the
                period.
            </div>`;
    }

    const formRef = useRef(undefined);

    const { patch: review, isSubmitting } = usePatch('/evidence/review');

    const formData = useMemo(() => {
        if (rowData?.ratingId) {
            const ratingObject = RatingsFromId(rowData.ratingId);
            return {
                rating: [ratingObject],
                uploadEvidence: rowData.evidence ?? null,
                legalEntityAssessmentId: rowData.legalEntityAssessmentId ?? null,
                subcomponentId: rowData.subcomponentId ?? null,
                haveReviewed: rowData.haveReviewed ?? null,
                canAccept: rowData.canAccept ?? null,
                comment: '',
                rationale: rowData.comment ?? 'No comment provided',
            };
        }
    }, [rowData]);

    const reviewMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await review(payload);
            return response?.data ?? response;
        },
        onSuccess: (response) => {
            const key = getCache(CacheKeyNames.legalEntityAssessmentData).cacheKey;
            if (!key && !response) return;
            try {
                onSuccessfulResponse(queryClient, key, updateEvidence, response);
            } catch (error) {
                console.error('Error updating cache:', error);
            }
        },
        onSettled: () => {
            const key = getCache(CacheKeyNames.legalEntityAssessmentData).cacheKey;
            queryClient.invalidateQueries({ queryKey: key });
        },
    });

    const handleSubmit = useCallback(
        (data) => {
            const payload = {
                attestationRecordId: rowData.attestationRecordId,
                evidenceId: rowData.evidence?.evidenceId,
                rating: data.rating?.[0]?.id ? [{ value: data.rating[0].id }] : [],
                haveReviewed: data.haveReviewed,
                canAccept: data.canAccept,
                comment: data.comment,
            };
            reviewMutation.mutate(payload);
        },
        [reviewMutation, rowData.attestationRecordId, rowData.evidence]
    );

    return formData?.uploadEvidence
        ? html`
              <div class="card-body">
                  <${Form}
                      ref=${formRef}
                      schema=${ReviewEvidenceFormSchema}
                      onSubmit=${handleSubmit}
                      initialValues=${formData}
                      disabled=${isSubmitting || props?.readonly}
                      options=${{
                          ...ReviewEvidenceFormOptions,
                          formProps: {
                              ...ReviewEvidenceFormOptions.formProps,
                              disabled: isSubmitting || props?.readonly,
                          },
                      }}
                  />
              </div>
              <div class="card-footer">
                  <${Button}
                      label=${{ text: 'Cancel' }}
                      buttonType="secondary"
                      onClick=${() => formRef?.current?.reset()}
                  />
                  <${Button}
                      label=${{ text: 'Submit' }}
                      buttonType="primary"
                      disabled=${isSubmitting || props?.readonly}
                      onClick=${(event) => formRef?.current?.handleSubmit(event)}
                  />
              </div>
          `
        : nothing;
};

export default ReviewEvidenceForm;
